import type * as React from 'react';
import { cn } from '../../lib/cn.ts';
import { Card } from '../card.tsx';
import { Sparkline } from '../charts/sparkline.tsx';
import { Delta, type DeltaProps } from './delta.tsx';

export interface MetricCardProps {
  label: string;
  value: React.ReactNode;
  delta?: { value: string; tone?: DeltaProps['tone'] };
  /** Sparkline series; omitted → no sparkline. */
  trend?: number[];
  /** Custom content under the value (e.g. language pills). Replaces the sparkline area when present. */
  children?: React.ReactNode;
  className?: string;
  'data-testid'?: string;
}

/**
 * MetricCard — a printed KPI figure with a calm delta and an optional sparkline
 * (or custom footer content, e.g. language pills).
 */
export function MetricCard({
  label,
  value,
  delta,
  trend,
  children,
  className,
  'data-testid': testId,
}: MetricCardProps) {
  return (
    <Card data-testid={testId} className={cn('flex flex-col gap-4 p-5', className)}>
      <p className="font-[var(--font-mono)] text-[10px] font-medium uppercase tracking-widest text-[var(--color-stone-mid)]">
        {label}
      </p>
      <div className="flex items-end gap-2">
        <span
          data-testid={testId ? `${testId}-value` : undefined}
          className="font-[var(--font-display)] text-4xl font-medium leading-none tracking-tight text-[var(--color-charcoal)]"
        >
          {value}
        </span>
        {delta && (
          <Delta
            data-testid={testId ? `${testId}-delta` : undefined}
            value={delta.value}
            tone={delta.tone}
            className="mb-1"
          />
        )}
      </div>
      {children ? (
        <div className="mt-auto">{children}</div>
      ) : trend && trend.length > 1 ? (
        <Sparkline
          data={trend}
          width={140}
          height={28}
          stretch
          strokeClassName="stroke-[var(--color-stone-400)]"
          className="mt-auto"
          ariaLabel={`${label} recent trend`}
        />
      ) : null}
    </Card>
  );
}
